import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Radar } from 'lucide-react';
import { ApiError, premiumApi, type PremiumBillingMode } from '../lib/api';
import { Alert, Button, Modal, Spinner } from './ui';

type ErrKind = 'insufficient' | 'paygSetup' | 'notFound' | 'generic';

/**
 * Premium-Button je Standard-Zeile (D7/AK3). Klick öffnet ein Bestätigungs-Modal mit dem
 * 3,50-€-Hinweis (Text je Org-Abrechnungsmodus). Bestätigen ruft `premiumApi.activate` — KEIN
 * optimistic UI: erst nach Server-200 lädt der Aufrufer den Container neu (→ Premium-Tabelle).
 * Fehler werden inline als Alert mit passender CTA gezeigt, ohne eine Belastung zu suggerieren.
 * White-Label: nur „Premium", nie der Anbietername.
 */
export function PremiumActivateButton({
  containerNumber,
  billingMode,
  token,
  onActivated,
}: {
  containerNumber: string;
  billingMode: PremiumBillingMode;
  token?: string | null;
  onActivated: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<ErrKind | null>(null);
  const [errMsg, setErrMsg] = useState('');
  const [settingUp, setSettingUp] = useState(false);

  function close(): void {
    if (busy) return;
    setOpen(false);
    setErr(null);
    setErrMsg('');
  }

  async function onConfirm(): Promise<void> {
    setBusy(true);
    setErr(null);
    try {
      await premiumApi.activate(containerNumber, token);
      setOpen(false);
      onActivated();
    } catch (e) {
      if (e instanceof ApiError && e.httpStatus === 402 && e.code === 'insufficient_funds') {
        setErr('insufficient');
      } else if (e instanceof ApiError && e.code === 'payg_setup_required') {
        setErr('paygSetup');
      } else if (e instanceof ApiError && e.httpStatus === 422) {
        setErr('notFound');
      } else {
        setErr('generic');
        setErrMsg(e instanceof ApiError ? e.message : String(e));
      }
    } finally {
      setBusy(false);
    }
  }

  async function onSetupPayment(): Promise<void> {
    setSettingUp(true);
    try {
      const { url } = await premiumApi.paygSetup(token);
      window.location.href = url;
    } catch (e) {
      setErr('generic');
      setErrMsg(e instanceof ApiError ? e.message : String(e));
      setSettingUp(false);
    }
  }

  return (
    <>
      <Button
        variant="default"
        size="sm"
        className="bg-accent text-accent-foreground hover:bg-accent/80"
        title={t('premium.button')}
        aria-label={t('premium.button')}
        onClick={() => setOpen(true)}
      >
        <Radar className="h-4 w-4" />
        <span className="hidden sm:inline">{t('premium.button')}</span>
      </Button>

      <Modal
        open={open}
        onClose={close}
        size="sm"
        title={t('premium.confirmTitle')}
        footer={
          err === null ? (
            <>
              <Button variant="secondary" onClick={close} disabled={busy}>
                {t('premium.cancel')}
              </Button>
              <Button onClick={() => void onConfirm()} disabled={busy}>
                {busy ? <Spinner /> : null}
                {busy ? t('premium.activating') : t('premium.confirm')}
              </Button>
            </>
          ) : (
            <Button variant="secondary" onClick={close}>
              {t('premium.cancel')}
            </Button>
          )
        }
      >
        {err === null ? (
          <p className="text-sm text-muted-foreground">
            {billingMode === 'payg' ? t('premium.confirmPayg') : t('premium.confirmWallet')}
          </p>
        ) : err === 'insufficient' ? (
          <Alert kind="warn">
            <div className="flex flex-col gap-2">
              <span>{t('premium.errInsufficient')}</span>
              <Button size="sm" variant="secondary" onClick={() => navigate('/billing')}>
                {t('premium.topup')}
              </Button>
            </div>
          </Alert>
        ) : err === 'paygSetup' ? (
          <Alert kind="warn">
            <div className="flex flex-col gap-2">
              <span>{t('premium.errPaygSetup')}</span>
              <Button
                size="sm"
                variant="secondary"
                disabled={settingUp}
                onClick={() => void onSetupPayment()}
              >
                {settingUp ? <Spinner /> : null}
                {t('premium.setupPayment')}
              </Button>
            </div>
          </Alert>
        ) : err === 'notFound' ? (
          <Alert kind="info">{t('premium.notFound')}</Alert>
        ) : (
          <Alert kind="error">{errMsg || t('premium.notFound')}</Alert>
        )}
      </Modal>
    </>
  );
}
